// Governance Phase 2: decisions (ADR 0029; plan §4, §5 "Decisions", §6.5,
// §6.6, §6.9, §11). Motions and their recorded votes, email meetings,
// unanimous written consent, the resolution register, minutes, and
// conflict-of-interest declarations.
//
// The pure rules here (which threshold applies, how a vote is counted, how
// an email meeting is decided, how a resolution is numbered) are what
// Platform Core decides with. The staff UI and myBRE only explain them.
// Every rule names the bylaws section it comes from.

import type { GovernanceOffice, GovernanceSettings, MeetingBody, PersonRef, VoteThreshold } from './governance'
import { boardQuorum, OFFICE_LABELS } from './governance'
import type { MotionProcedureView } from './governance-unfinished'

// --------------------------------------------------------------- vocabulary

export const VOTE_CHOICES = ['yea', 'nay', 'abstain', 'recused'] as const
export type VoteChoice = (typeof VOTE_CHOICES)[number]
export const VOTE_CHOICE_LABELS: Record<VoteChoice, string> = { yea: 'Yes', nay: 'No', abstain: 'Abstain', recused: 'Recused (conflict of interest)' }

// How a vote reached Hub: the director in myBRE; the ED recording a voice
// or roll-call vote at the meeting; the ED recording a director's vote on
// their behalf (a director without myBRE), with an attestation.
export const VOTE_CHANNELS = ['mybre', 'roll_call', 'attested'] as const
export type VoteChannel = (typeof VOTE_CHANNELS)[number]

export const MOTION_STATUSES = ['draft', 'open', 'adopted', 'failed', 'no_quorum', 'withdrawn'] as const
export type MotionStatus = (typeof MOTION_STATUSES)[number]
export const MOTION_FINAL_STATUSES: MotionStatus[] = ['adopted', 'failed', 'no_quorum', 'withdrawn']
export const MOTION_STATUS_LABELS: Record<MotionStatus, string> = { draft: 'Draft', open: 'Voting open', adopted: 'Adopted', failed: 'Failed', no_quorum: 'No quorum', withdrawn: 'Withdrawn' }

// Planned motions (ADR 0029, note of 2026-10-05): a draft motion prepared
// on an agenda item while the meeting is still scheduled. It is a draft,
// not a record: changed or removed only until the meeting is called to
// order; one never moved lapses at adjournment as "not moved" and, like any
// motion never made, stays out of the minutes (Robert's Rules: the minutes
// record motions made, not motions contemplated).
export const MOTION_NOT_MOVED_REASON = 'Not taken up before the meeting adjourned'
export const MOTION_REMOVED_REASON = 'Removed from the agenda before the meeting'

// A planned motion as directors read it before the meeting, in the packet
// and in myBRE, labeled "Proposed motion". Never the suggested mover (a
// note for the ED, not a commitment by a director).
export type ProposedMotionView = {
  id: string
  motionType: string
  typeLabel: string
  title: string
  text: string
  threshold: string | null
  subjectName: string | null
  // ADR 0059: "Recommended by the Riverfront Rampage Committee (RR-2027-03)"
  // on a board motion, or "Recommendation to the board" on a committee's.
  label?: string
}

// Motion types that need a person named: the director the motion concerns.
export const SUBJECT_MOTION_TYPES = ['remove', 'terminate_absences']

// Unanimous written consent (plan §11 item 5): built, hidden unless the
// settings in effect turn it on. Not a settings threshold: it is unanimity
// of every director seated, by definition.
export const WRITTEN_CONSENT_TYPE = 'unanimous_written_consent'

export const EMAIL_MEETING_KINDS = ['email_meeting', 'written_consent'] as const
export type EmailMeetingKind = (typeof EMAIL_MEETING_KINDS)[number]
export const EMAIL_MEETING_STATUSES = ['draft', 'awaiting_officer', 'open', 'closed', 'canceled'] as const
export type EmailMeetingStatus = (typeof EMAIL_MEETING_STATUSES)[number]
export const EMAIL_MEETING_STATUS_LABELS: Record<EmailMeetingStatus, string> = {
  draft: 'Draft',
  awaiting_officer: 'Waiting for the officer to send',
  open: 'Open for votes',
  closed: 'Closed',
  canceled: 'Canceled',
}

export const MINUTES_STATUSES = ['draft', 'approved', 'signed'] as const
export type MinutesStatus = (typeof MINUTES_STATUSES)[number]

// Motion types a committee other than the Executive Committee may use:
// committees recommend; the board decides (Article V).
export const COMMITTEE_MOTION_TYPES = ['ordinary']

// The Executive Committee holds the board's powers between meetings,
// except amending the articles or the bylaws (Article V, Section 2).
export const EXECUTIVE_BLOCKED_TYPES = ['amend_bylaws']

// ------------------------------------------------------------- thresholds

export type ThresholdView = VoteThreshold & {
  // In words, for the ballot and the minutes: "Two-thirds of the directors
  // present (Article X)".
  description: string
  settingsVersion: number
}

export function thresholdFor(settings: GovernanceSettings, motionType: string): VoteThreshold | null {
  return settings.voteThresholds.find((t) => t.motionType === motionType) ?? null
}

const FRACTION_WORDS: Record<string, string> = { '1/2': 'half', '2/3': 'two-thirds', '3/4': 'three-fourths' }

// The threshold in plain words, for a meeting body or an email meeting.
export function describeThreshold(t: VoteThreshold, context: 'board' | 'committee' | 'email'): string {
  const fraction = FRACTION_WORDS[`${t.numerator}/${t.denominator}`] ?? `${t.numerator}/${t.denominator}`
  const share = t.rule === 'majority' ? 'A majority' : `${fraction[0]!.toUpperCase()}${fraction.slice(1)}`
  const who = context === 'committee' ? 'members' : 'directors'
  const base =
    context === 'email'
      ? t.base === 'board'
        ? `of all ${who} seated`
        : t.base === 'remaining_directors'
          ? `of the remaining ${who}`
          : `of the ${who} who respond by the deadline, with responders a quorum`
      : t.base === 'present_and_voting'
        ? `of the ${who} present and voting, with quorum`
        : t.base === 'present'
          ? `of the ${who} present, with quorum`
          : t.base === 'board'
            ? context === 'committee'
              ? `of all voting ${who} of the committee`
              : `of all ${who} seated (the whole board)`
            : t.base === 'remaining_directors'
              ? `of the remaining ${who}`
              : `of the ${who} who respond`
  return `${share} ${base}`
}

export function thresholdView(settings: GovernanceSettings, settingsVersion: number, motionType: string, context: 'board' | 'committee' | 'email'): ThresholdView | null {
  if (motionType === WRITTEN_CONSENT_TYPE) {
    const t: VoteThreshold = { motionType, label: 'Unanimous written consent', numerator: 1, denominator: 1, rule: 'at_least', base: 'board', noticeDays: null, source: settings.unanimousWrittenConsent.source }
    return { ...t, description: 'Every director seated, in writing', settingsVersion }
  }
  const t = thresholdFor(settings, motionType)
  return t ? { ...t, description: describeThreshold(t, context), settingsVersion } : null
}

// ---------------------------------------------------------------- counting

export type VoteTally = { yea: number; nay: number; abstain: number; recused: number }

export function tally(choices: VoteChoice[]): VoteTally {
  const t: VoteTally = { yea: 0, nay: 0, abstain: 0, recused: 0 }
  for (const c of choices) t[c] += 1
  return t
}

// Recusals for display (owner, 2026-10-25). A director is recused who voted
// "recused" or declared a conflict on the motion; one who declares a
// conflict without voting has no vote row, so the stored tally misses them.
// Display only: no threshold uses recusals except as responses to an email
// meeting (emailMeetingResult, respondedCount).
export function recusedCount(votes: { person: { id: string }; choice: VoteChoice }[], conflicts: { person: { id: string } }[]): number {
  const ids = new Set<string>()
  for (const v of votes) if (v.choice === 'recused') ids.add(v.person.id)
  for (const c of conflicts) ids.add(c.person.id)
  return ids.size
}

export function withRecusals(t: VoteTally, recused: number): VoteTally {
  return { ...t, recused: Math.max(t.recused, recused) }
}

// The directors who responded to an email meeting (owner, 2026-10-25: "they
// count"): everyone who voted (yes, no, abstain or recused) and everyone who
// declared a conflict without voting, once each, among the recipients.
export function respondedCount(voterIds: Iterable<string>, conflictIds: Iterable<string>, recipientIds?: Iterable<string>): number {
  const ids = new Set<string>([...voterIds, ...conflictIds])
  if (!recipientIds) return ids.size
  const to = new Set(recipientIds)
  let n = 0
  for (const id of ids) if (to.has(id)) n += 1
  return n
}

// "6 yes, 1 no, 1 abstain, 1 recused": yes and no always, the others when
// there are any.
export function tallyText(t: VoteTally): string {
  return [`${t.yea} yes`, `${t.nay} no`, t.abstain > 0 ? `${t.abstain} abstain` : null, t.recused > 0 ? `${t.recused} recused` : null].filter(Boolean).join(', ')
}

export type VoteResult = {
  passed: boolean
  outcome: 'adopted' | 'failed' | 'no_quorum'
  tally: VoteTally
  // What the threshold is measured against, and how many yes votes it took.
  base: number
  baseLabel: string
  required: number
  // Quorum when the vote closed.
  quorum: { required: number; present: number; met: boolean }
  explanation: string
}

// The yes votes a threshold needs out of `base`: a majority is more than
// half; "at least" a fraction is that share rounded up.
export function yesVotesNeeded(t: Pick<VoteThreshold, 'rule' | 'numerator' | 'denominator'>, base: number): number {
  if (base <= 0) return 1
  if (t.rule === 'majority') return Math.floor((base * t.numerator) / t.denominator) + 1
  return Math.ceil((base * t.numerator) / t.denominator)
}

// A vote at a meeting (Robert's Rules, Article VII, Section 2; the
// thresholds of plan §6.5). Counted only among the voting members: ex
// officio, non-voting members never vote and never count.
//
//   present_and_voting  yes and no votes cast; abstentions and recusals don't count
//   present             the voting members present when the vote closed
//   board               every voting member seated on the meeting date
//   remaining_directors those seated, less the director the motion would remove
//
// No business without quorum (Article III, Section 5): a vote that closes
// without quorum decides nothing.
export function meetingVoteResult(input: {
  threshold: Pick<VoteThreshold, 'rule' | 'numerator' | 'denominator' | 'base' | 'source'>
  votes: VoteTally
  presentVoting: number
  seatedVoting: number
  quorumRequired: number
  subjectIsSeated?: boolean
}): VoteResult {
  const { threshold: t, votes } = input
  const quorum = { required: input.quorumRequired, present: input.presentVoting, met: input.quorumRequired > 0 && input.presentVoting >= input.quorumRequired }
  const [base, baseLabel] =
    t.base === 'present_and_voting'
      ? [votes.yea + votes.nay, 'votes cast (yes and no)']
      : t.base === 'present'
        ? [input.presentVoting, 'voting members present']
        : t.base === 'board'
          ? [input.seatedVoting, 'voting members seated']
          : t.base === 'remaining_directors'
            ? [input.seatedVoting - (input.subjectIsSeated ? 1 : 0), 'remaining directors']
            : [votes.yea + votes.nay + votes.abstain + votes.recused, 'responses']
  const required = yesVotesNeeded(t, base)
  if (!quorum.met) {
    return { passed: false, outcome: 'no_quorum', tally: votes, base, baseLabel, required, quorum, explanation: `No quorum when the vote closed (${quorum.present} of ${quorum.required} needed)` }
  }
  const passed = base > 0 && votes.yea >= required && votes.yea > votes.nay
  return {
    passed,
    outcome: passed ? 'adopted' : 'failed',
    tally: votes,
    base,
    baseLabel,
    required,
    quorum,
    explanation: `${votes.yea} yes of ${base} ${baseLabel}; ${required} needed`,
  }
}

// An email meeting (Article III, Section 9; owner reading, plan §11 item 2):
// quorum is the directors who responded by the deadline; the motion passes
// with a majority of those who responded, provided they are a quorum. A
// motion type with a higher threshold applies it as at a meeting: "of the
// board" counts every director seated when it was sent, "remaining
// directors" those less the one to be removed, and "present" (or present
// and voting) the responders. Every response counts as a response,
// abstentions and recusals included.
//
// A director who only declares a conflict (no vote row) counts as having
// responded, toward the quorum of responders, but is not in any base: the
// threshold counts the votes cast exactly as before (owner, 2026-10-25).
export function emailMeetingResult(input: {
  threshold: Pick<VoteThreshold, 'rule' | 'numerator' | 'denominator' | 'base' | 'source'>
  votes: VoteTally
  seated: number
  quorumPercent: number
  subjectIsSeated?: boolean
  // Directors among the recipients who declared a conflict and never voted.
  conflictsWithoutVote?: number
  // Who the recipients are, in words: directors (the board), or members (a
  // committee's vote by email, ADR 0059).
  noun?: 'directors' | 'members'
}): VoteResult {
  const { threshold: t, votes } = input
  const noun = input.noun ?? 'directors'
  const voted = votes.yea + votes.nay + votes.abstain + votes.recused
  const conflicted = Math.max(0, input.conflictsWithoutVote ?? 0)
  const responders = voted + conflicted
  const required0 = boardQuorum(input.seated, input.quorumPercent)
  const quorum = { required: required0, present: responders, met: required0 > 0 && responders >= required0 }
  const [base, baseLabel] =
    t.base === 'board'
      ? [input.seated, noun === 'directors' ? 'directors seated' : 'voting members']
      : t.base === 'remaining_directors'
        ? [input.seated - (input.subjectIsSeated ? 1 : 0), 'remaining directors']
        : [voted, conflicted > 0 ? `${noun} who responded with a vote` : `${noun} who responded`]
  const required = yesVotesNeeded(t, base)
  const recusedNote = conflicted > 0 ? ` (${conflicted} by declaring a conflict)` : ''
  if (!quorum.met) {
    return { passed: false, outcome: 'no_quorum', tally: votes, base, baseLabel, required, quorum, explanation: `${responders} ${noun} responded by the deadline${recusedNote}; a quorum is ${required0}` }
  }
  const passed = base > 0 && votes.yea >= required && votes.yea > votes.nay
  return { passed, outcome: passed ? 'adopted' : 'failed', tally: votes, base, baseLabel, required, quorum, explanation: `${votes.yea} yes of ${base} ${baseLabel}; ${required} needed` }
}

// Unanimous written consent (plan §11 item 5): every director seated when
// it was sent consents in writing. One "no", abstention, recusal or
// silence and it fails.
export function writtenConsentResult(input: { votes: VoteTally; seated: number; source: string }): VoteResult {
  const { votes, seated } = input
  const passed = seated > 0 && votes.yea === seated
  return {
    passed,
    outcome: passed ? 'adopted' : 'failed',
    tally: votes,
    base: seated,
    baseLabel: 'directors seated',
    required: seated,
    quorum: { required: seated, present: votes.yea + votes.nay + votes.abstain + votes.recused, met: true },
    explanation: passed ? `All ${seated} directors consented in writing` : `${votes.yea} of ${seated} directors consented; it must be unanimous`,
  }
}

// ---------------------------------------------------------- the register

// Resolutions are numbered within the fiscal year they were adopted in
// (FY2027 is July 1, 2026 to June 30, 2027): R-2027-001, R-2027-002, ...
// The Executive Committee has its own series, EC-2027-001 (the resolution
// book, ADR 0048); historical ones entered from records carry H-.
export const resolutionNumber = (fiscalYear: number, seq: number, series: 'R' | 'EC' | 'H-R' | 'H-EC' = 'R') => `${series}-${fiscalYear}-${String(seq).padStart(3, '0')}`

// ------------------------------------------------------------------ shapes

export type MotionView = {
  id: string
  title: string
  // Null for a reader without governance.confidential when the motion was
  // made in executive session.
  text: string | null
  executive: boolean
  redacted: boolean
  motionType: string
  typeLabel: string
  body: MeetingBody
  committeeName: string | null
  meetingId: string | null
  meetingTitle: string | null
  emailMeetingId: string | null
  agendaItemId: string | null
  status: MotionStatus
  mover: PersonRef | null
  seconder: PersonRef | null
  threshold: ThresholdView | null
  openedAt: string | null
  closedAt: string | null
  result: VoteResult | null
  // Named votes, as the minutes record them (a roll call).
  // unanimous: recorded by the conductor's "Unanimous: everyone present
  // votes yes" (general consent), still one recorded yes per director.
  votes: { person: PersonRef; choice: VoteChoice; channel: VoteChannel; at: string; unanimous: boolean }[]
  conflicts: { person: PersonRef; declaredAt: string; note: string | null }[]
  subject: PersonRef | null
  overrideTerm: { personId: string; seatKind: 'elected' | 'appointed'; termStartsOn: string } | null
  minutesId: string | null
  amendsArticles: boolean
  noticeOfIntent: { sentAt: string; method: string; days: number; qualifies: boolean; reason: string } | null
  resolution: { id: string; number: string } | null
  // Who may still vote and hasn't, for the conductor.
  awaiting: PersonRef[]
  // Prepared before the meeting was called to order (a planned motion).
  planned: boolean
  // A hint for the ED; the mover is chosen when the motion is made.
  suggestedMover: PersonRef | null
  // A planned motion that lapsed at adjournment without being moved.
  notMoved: boolean
  // Taken off the agenda while the meeting was still being planned.
  removed: boolean
  // For an edit of a planned motion (expectedVersion).
  version: number
  createdAt: string
  // Phase 3 (ADR 0030): an election motion (the slate, one slot, or the
  // order for a ballot), or a director vacancy appointment.
  electionId: string | null
  electionRole: 'slate' | 'slot' | 'ballot' | null
  vacancyId: string | null
  appointee: PersonRef | null
  // ADR 0031: the consent agenda's single motion (and the items it
  // adopted, once its vote opened); a subsidiary motion to lay on the table
  // or postpone (and the motion it disposes of); a main motion so disposed
  // of; a motion carried to this meeting from an earlier one.
  consent: boolean
  consentItems: { agendaItemId: string; position: number; title: string; minutesId: string | null }[]
  disposesMotionId: string | null
  disposition: 'tabled' | 'postponed' | null
  carriedFrom: { motionId: string; meetingId: string | null; meetingTitle: string | null; disposition: 'tabled' | 'postponed' | null } | null
  // ADR 0048 (the resolution book): the kind (a decision is numbered;
  // procedural and routine approvals are recorded only), fixed once the
  // vote opens; what the motion names (an earlier resolution it amends,
  // rescinds, supersedes, ratifies or accepts the report of, or a policy it
  // adopts, amends or retires); whether it authorizes the Executive
  // Committee to act on it; and when a time-limited authority expires.
  registerKind: 'decision' | 'procedural' | 'routine_approval'
  registerKindDefault: 'decision' | 'procedural' | 'routine_approval'
  links: { id: string; effect: string; label: string; resolution: { id: string; number: string; title: string } | null; policy: { id: string; title: string } | null }[]
  authorizesExecutive: boolean
  expiresOn: string | null
  // ADR 0059: a committee motion's number in the committee register once
  // adopted (RR-2027-01, never an R- number); whether it is a
  // "Recommendation to the board"; what earlier committee decisions it
  // names; and, on a board motion, the committee recommendation it came from.
  committeeDecision?: { id: string; number: string } | null
  recommendsToBoard?: boolean
  // ADR 0067: an Executive Committee motion "Subject to the board's
  // ratification" (fixed once the vote opens).
  subjectToRatification?: boolean
  committeeLinks?: { id: string; effect: string; label: string; decision: { id: string; number: string; title: string } }[]
  // ADR 0069: from the Executive Committee, the EC- resolution
  // (`resolutionId`; `decisionId` is empty).
  recommendedBy?: { decisionId: string; number: string; committeeName: string; resolutionId?: string } | null
  // ADR 0065: what a procedural motion does to the motion it names; a
  // motion's earlier wording (each amendment adopted); the vote a
  // reconsideration reopened; and what a motion set aside reads.
  procedure?: MotionProcedureView | null
  textHistory?: { version: number; title: string; text: string; amendmentMotionId: string | null }[]
  reopenedFrom?: { reconsiderMotionId: string; originalMotionId: string; originalMeetingId: string | null; originalMeetingStartsLocal: string | null } | null
  dispositionLabel?: string | null
}

export type EmailMeetingView = {
  id: string
  kind: EmailMeetingKind
  status: EmailMeetingStatus
  motion: MotionView
  deadlineLocal: string
  deadlineAt: string
  draftedBy: PersonRef | null
  draftedAt: string
  senderOffice: 'secretary' | 'president' | 'executive_director' | null
  sender: PersonRef | null
  sentAt: string | null
  requestedAt: string | null
  requestAttestation: string | null
  recipients: PersonRef[]
  responded: number
  quorumRequired: number
  closedAt: string | null
  closedReason: string | null
  reportedMeeting: { id: string; title: string } | null
  canceledAt: string | null
  cancelReason: string | null
  // ADR 0070: an officer's authorization for the Executive Director to send
  // a board email meeting from Hub. Null when there is none, and on one an
  // officer sent themselves.
  sendAuthorization: EmailMeetingSendAuthorization | null
}

export const SEND_AUTHORIZATION_METHODS = ['mybre', 'recorded'] as const
export type SendAuthorizationMethod = (typeof SEND_AUTHORIZATION_METHODS)[number]

export type EmailMeetingSendAuthorization = {
  office: GovernanceOffice
  officer: PersonRef
  givenOn: string
  // 'mybre': the officer's own tap; 'recorded': recorded by the ED, attested.
  method: SendAuthorizationMethod
  how: string | null
  attestation: string | null
  recordedBy: PersonRef | null
  recordedAt: string
  // Why it no longer lets the ED send (the draft changed, the officer left
  // the office, the settings changed); null while it does, and once used.
  lapsed: string | null
  // The ED sent the email meeting on it.
  used: boolean
}

// "on the Secretary's authorization": the minutes, Hub and myBRE.
export const authorizationPhrase = (office: GovernanceOffice) => `on the ${OFFICE_LABELS[office]}'s authorization`

export type ResolutionView = {
  id: string
  number: string
  series: 'R' | 'EC' | 'H-R' | 'H-EC'
  origin: 'hub' | 'records'
  fiscalYear: number
  adoptedOn: string
  title: string
  text: string | null
  executive: boolean
  redacted: boolean
  source: 'meeting' | 'email_meeting' | 'written_consent'
  body: MeetingBody
  motionId: string | null
  meetingId: string | null
  meetingTitle: string | null
  emailMeetingId: string | null
  tally: VoteTally | null
}

export type MinutesSection = { key: string; title: string; body: string; executive: boolean }

// ADR 0031: a section's place in the flexible minutes. Generated sections
// (every key not starting "custom_") stay regenerable and come out of a
// draft only with a note.

export type MinutesView = {
  id: string
  meetingId: string
  meetingTitle: string
  meetingStartsLocal: string
  status: MinutesStatus
  version: number
  sections: (MinutesSection & { redacted: boolean })[]
  hash: string
  approval: { motionId: string; meetingId: string | null; meetingTitle: string | null; adoptedAt: string | null; resolutionNumber: string | null } | null
  signature: { person: PersonRef; signedAt: string; channel: 'mybre' | 'attested'; attestation: string | null; hash: string } | null
  corrections: { id: string; body: string; reason: string; at: string; by: string | null; motionId: string | null }[]
  versions: { version: number; createdAt: string; createdBy: string | null }[]
  canEdit: boolean
  // ADR 0031: generated sections taken out of this version, with the note;
  // the draft marked ready for the directors; the President's signature
  // (optional unless the practice setting requires it).
  removed: { key: string; title: string; note: string }[]
  reviewReady: { at: string; version: number } | null
  countersignature: { role: 'president'; person: PersonRef; signedAt: string; channel: 'mybre' | 'attested'; attestation: string | null; hash: string } | null
  presidentSignature: 'optional' | 'required'
  // A committee's minutes (the Executive Committee's included): approved by
  // the committee and signed by its chair alone. Null for the board's.
  committee: { id: string; name: string } | null
}

export type DisclosureAnswers = Record<string, string | string[] | number | boolean | null>

export type DisclosureView = {
  id: string
  person: PersonRef
  fiscalYear: number
  filedOn: string
  channel: 'mybre' | 'attested' | 'paper'
  formVersion: number | null
  answers: DisclosureAnswers | null
  note: string | null
  onBehalf: boolean
  updates: { id: string; filedAt: string; formVersion: number | null; answers: DisclosureAnswers }[]
}

// The disclosure form, internal to governance: never on the forms host.
export const COI_FORM_ID = 'frm_governance_coi'

// Version 1 of the annual conflict of interest disclosure (owner reading,
// plan §11 item 6), seeded by migration 0047 exactly as the forms engine
// would publish it (a test holds the two equal). Later versions are
// published from Governance → Disclosures; a filing names the version it
// answered. The answers are confidential (plan §6.9).
export const COI_FORM_DEFINITION = {
  title: 'Annual conflict of interest disclosure',
  intro:
    'Every director files this once each fiscal year (July 1 to June 30). It is read only by the Executive Director and the Executive Committee, and is never shared with other directors or staff. If something changes during the year, file an update; on a particular motion, declare the conflict before the vote and you will be recused.',
  fields: [
    { key: 'employment', type: 'long_text', label: 'Your employers, and businesses you own, manage or advise (with your role)', help: 'Include your spouse or partner and dependents where you know of them.', required: false, sensitive: true },
    { key: 'other_boards', type: 'long_text', label: 'Other boards or committees you serve on, nonprofit or for-profit', required: false, sensitive: true },
    { key: 'bre_relationships', type: 'long_text', label: 'Business or financial relationships you or a family member have with BRE, its vendors, tenants, sponsors or funders', required: false, sensitive: true },
    { key: 'gifts', type: 'long_text', label: 'Gifts, loans or favors worth more than $100 in the last year from anyone doing business with BRE', required: false, sensitive: true },
    { key: 'nothing_to_disclose', type: 'checkbox', label: 'I have nothing to disclose beyond what I wrote above', required: false, sensitive: false, purpose: 'acknowledgement' },
    {
      key: 'certify',
      type: 'checkbox',
      label: "I have read BRE's conflict of interest policy. This disclosure is complete and true, I will update it if anything changes, and I will declare any conflict on a motion before the board votes.",
      required: true,
      sensitive: false,
      purpose: 'acknowledgement',
    },
  ],
  confirmationMessage: 'Thank you. Your disclosure is filed for this fiscal year.',
  submitLabel: 'File my disclosure',
} as const
